import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import PieChartV2 from 'components/charts/chartJS/PieChart';
import { chartColors } from 'components/charts/chartJS/utils';
import DateFilter from 'components/field/DateFilter';
import EnumSelect from 'components/field/EnumSelect';
import Card from 'components/global/Card';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';
import useLocale from 'hooks/useLocale';

import { ENUMS, REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';

const SalesTab = () => {
	const { Currency } = useLocale();
	const [selectedCurrency, setSelectedCurrency] = useState(Currency);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(7, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(
		() => ({ from: filter?.start, to: filter?.end, currency: selectedCurrency }),
		[filter, selectedCurrency]
	);

	return (
		<WidgetPageContainer>
			<Typography fontWeight={700} variant="subtitle1">
				filterSaleByDate
			</Typography>
			{/* the following line in for grid layout (necessary for resizing feature) */}
			{/* <div style={{ gridColumn: 'span 6' }}> */}
			<div style={{ width: '100%' }}>
				<div style={{ display: 'flex', gap: '1rem' }}>
					<div style={{ width: 'min(166px, 100%)' }}>
						<DateFilter defaultDay="last7Days" setValue={setFilter} />
					</div>
					<div style={{ width: 'min(166px, 100%)' }}>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.CURRENCY}
							value={selectedCurrency}
							setValue={setSelectedCurrency}
						/>
					</div>
				</div>
			</div>
			<WidgetWrapper widgetId={'orderValuesSummary'}>
				<OrderValuesSummary body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'shipmentPaymentType'}>
				<ShipmentPaymentType body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'ordersByCity'}>
				<OrdersByCity body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'numberOfOrders'}>
				<NumberOfOrders body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'avgBasketSize'}>
				<AvgBasketSize body={body} />
			</WidgetWrapper>
		</WidgetPageContainer>
	);
};

const OrderValuesSummary = ({ body }) => {
	const cardsConfig = [
		{
			accessor: 'newReceived',
			label: 'newReceived',
			customIcon: 'fa-regular-box-circle-arrow-down',
			tooltip: 'dashboard.newReceived.description',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-down-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-down-color)'
		},
		{
			accessor: 'processing',
			label: 'processing',
			customIcon: 'fa-regular-loader-circle-bolt',
			tooltip: 'dashboard.processing.description',
			backgroundColor: 'var(--fa-regular-loader-circle-bolt-bgColor)',
			color: 'var(--fa-regular-loader-circle-bolt-color)'
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			customIcon: 'fa-regular-box-circle-check',
			tooltip: 'dashboard.delivered.description',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			tooltip: 'dashboard.returned.description',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)'
		},
		{
			accessor: 'canceled',
			label: 'canceled',
			customIcon: 'fa-regular-box-circle-xmark',
			tooltip: 'dashboard.canceled.description',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_TODAY_ACTIVITY,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader title={'orderValuesSummary'} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const pieChartAfterFetch = (data) => {
	const labels = data.map((item) => item.label);
	const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
	return { labels, datasets };
};

const ShipmentPaymentType = ({ body }) => {
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_FREE_VS_PAID_SHIPPING,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'shipmentPaymentType'} titleTooltip={'analysis.shipmentPaymentTypeDescription'} />
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const OrdersByCity = ({ body }) => {
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_TOP_5_CITIES,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'ordersByCity'} titleTooltip={'analysis.ordersByCityDescription'} />
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const barChartAfterFetch = (data, t) => {
	const labels = data.map((item) => item.xAxis);
	const datasets = [
		{
			label: t('avgBasketSize'),
			data: data.map((item) => item.yAxis),
			backgroundColor: colorCombinations[0].color,
			borderColor: colorCombinations[0].color
		}
	];
	return { labels, datasets };
};

const NumberOfOrders = ({ body }) => {
	const { t } = useTranslation();

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_NUMBER_OF_ORDERS,
		body,
		parseData: (data) => barChartAfterFetch(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'numberOfOrders'} titleTooltip={'analysis.numberOfOrdersDescription'} />
			<BarChartV2 xTitle="date" yTitle="numberOfOrders" data={data} loading={loading} />
		</Card>
	);
};

const AvgBasketSize = ({ body }) => {
	const { t } = useTranslation();

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_AVG_BASKET_SIZE,
		body,
		parseData: (data) => barChartAfterFetch(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'avgBasketSize'} titleTooltip={'analysis.avgBasketSizeDescription'} />
			<BarChartV2 xTitle="date" yTitle="avgBasketSize" data={data} loading={loading} />
		</Card>
	);
};

export default SalesTab;
